<template>
    <div class="row">
        <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></loading>
        <confirm-box ref="error" :no-confirm="true"></confirm-box>


        <div class="d-none d-lg-block col-lg-2"></div>
        <div class="col-12 col-lg-8">
            <h1>Student Report</h1>

            <div class="row">
                <div class="mb-2 col-12">
                    <label for="district-select">District</label>
                    <select class="form-select" v-model="district" id="district-select">
                        <option v-for="dist in districts" :value="dist.id">{{ dist.name }}</option>
                    </select>                
                </div>

                <div class="mb-2 col-12">
                    <label for="student-select">Students</label>
                    <multiselect
                        v-model="studentsAdded"
                        :searchable="true"
                        :options="studentsAvailable"
                        :multiple="true"
                        :loading="isLoading"
                        :options-limit="50"
                        :internal-search="false"
                        :clear-on-select="false" 
                        :close-on-select="false" 
                        :custom-label="showStudents"
                        placeholder="Select students.."
                        track-by="id"
                        label="name"
                        @search-change="search"
                        id="student-select"
                    ></multiselect>
                </div>
            </div>

            <div class="mb-4 mt-4">
                <home-button class="me-2"></home-button>
                <button class="btn btn-primary" @click="runReport"><i class="fa-regular fa-file-lines me-2"></i>Run Report</button>
            </div>

            <div class="card mb-2" v-for="student in results">
                <div class="card-header d-flex align-items-baseline">
                    <span class="flex-grow-1">{{ student.last_name }}, {{ student.first_name }}</span>
                    <span class="badge bg-primary me-2">{{ student.assessments.length }}</span>
                    <a 
                        data-bs-toggle="collapse" 
                        aria-expanded="false" 
                        :aria-controls="`student-assessment-body-${student.id}`"
                        :href="`#student-assessment-body-${student.id}`" 
                        role="button"
                        class="collapsed collapse-toggle text-decoration-none text-dark"
                    >
                    </a>
                </div>

                <div class="collapse" :ref="`student-assessment-body-${student.id}`" :id="`student-assessment-body-${student.id}`">
                    <div class="card-body">

                        <dl class="row">
                            <dt class="col-sm-3">Graduation</dt>
                            <dd class="col-sm-9">{{ student.grad_year }}</dd>

                            <dt class="col-sm-3">Number</dt>
                            <dd class="col-sm-9">{{ student.number }}</dd>
                        </dl>

                        <h5 class="card-title">Assessments</h5>
                        <div v-for="(assessment, index) in student.assessments" class="card mb-2">
                            <div class="card-header">
                                <span class="text-primary">
                                    {{ assessment.code }}
                                    <span v-if="assessment.subcode"> {{ assessment.subcode }}</span>
                                </span>
                                <span class="fw-bold px-2">{{ assessment.strand }}</span>
                                <span class="text-muted">{{ assessment.area_name }} {{ assessment.area_state }}-{{ assessment.area_year }}</span>
                            </div>

                            <div class="p-2">{{ assessment.content }}</div>

                            <dl class="row mx-2">
                                <dt class="col-sm-3">Assessment</dt>
                                <dd v-if="assessment.assessment == 4" class="col-sm-9">
                                    <span class="badge bg-success">{{ assessment.assessment }}</span> Mastery understanding
                                </dd>
                                <dd v-else-if="assessment.assessment == 3" class="col-sm-9">
                                    <span class="badge bg-warning">{{ assessment.assessment }}</span> Adequate progress
                                </dd>
                                <dd v-else-if="assessment.assessment == 2" class="col-sm-9">
                                    <span class="badge bg-danger">{{ assessment.assessment }}</span> Not yet adequate, but showing some growth
                                </dd>
                                <dd v-else-if="assessment.assessment == 1" class="col-sm-9">
                                    <span class="badge bg-danger">{{ assessment.assessment }}</span> Not yet adequate, little or no growth
                                </dd>
                                <dd v-else-if="assessment.assessment == 0" class="col-sm-9">
                                    <span class="badge bg-info">{{ assessment.assessment }}</span> No evaluation
                                </dd>
                                <dd v-else="assessment.assessment == 4" class="col-sm-9">
                                    <span >{{ assessment.assessment }}</span>
                                </dd>

                                <template v-if="assessment.duration">
                                    <dt class="col-sm-3">Duration</dt>
                                    <dd class="col-sm-9">{{ assessment.duration }} minutes</dd>
                                </template>

                                <dt class="col-sm-3">Assessment Date</dt>
                                <dd class="col-sm-9">{{ assessment.evaluation_date }}</dd>

                                <dt class="col-sm-3">Evaluator</dt>
                                <dd class="col-sm-9">{{ assessment.evaluator }}</dd>

                                <dt class="col-sm-3">Notes</dt>
                                <dd class="col-sm-9">{{ assessment.evaulation_notes }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import Multiselect from 'vue-multiselect';

import HomeButton from './HomeButton.vue';
import ConfirmBox from './ConfirmBox.vue';

export default {
    components: { Loading, Multiselect, HomeButton, ConfirmBox },

    data() {
        return {
            isLoading: false,
            studentsAdded: [],
            studentsAvailable: [],
            districts: [],
            district: null,
            results: [],
        }
    },

    mounted() {
        this.isLoading = false;
        axios
            .get('/api/admin/users/allowed-districts')
            .then((response) => {
                this.districts = response.data;
                this.district = this.districts[0].id ?? null;
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                this.isLoading = false;
            });
    },

    methods: {
        search: function(text) {
            clearTimeout(this.searchTimeout);

            this.changeTimeout = setTimeout(() => {
                if (!text) {
                    console.log('search aborted');
                    return;
                }

                this.isLoading = true;
                axios
                    .get(`/api/students?search=${text}&limit=25&district=${this.district}`)
                    .then((response) => {
                        console.log(response.data);
                        this.studentsAvailable = response.data.students;
                    })
                    .catch((error) => {
                        console.error(error);
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }, 1000);
        },

        showStudents: function(student) {
            return `${student.first_name} ${student.last_name} (${student.number})`;
        },

        runReport: function() {
            this.isLoading = true;
            const students = this.studentsAdded.map((s) => s.id ?? null);
            axios
                .get(`/api/admin/students/report?students=${students}&district=${this.district}`)
                .then((response) => {
                    this.results = response.data;
                    console.log(this.results);
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.$refs.error.show({
                            title: 'Update Student Error',
                            content: error.response.data.message,
                            callback: () => {},
                        });
                    } else {
                        console.error(error);
                    }

                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

    }



}
</script>

<style>
    .card-header .collapse-toggle:after {
        content: '\f077';
        font-family: 'Font Awesome 6 Free';
        font-weight: 900;
    }

    .card-header .collapse-toggle.collapsed:after {
        content: '\f078';
    }
</style>